import React, { useState, useEffect } from 'react';
import { Card, Tabs, Button, Typography, Space, Row, Col, Table, Modal, message, Avatar, Popconfirm, Tooltip, Grid } from 'antd';
import { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';
// Use styled XLSX for bold headers
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyService from '../../services/CompanyService';
import { CheckCircleOutlined, MinusCircleOutlined, EditOutlined, DeleteOutlined, StarFilled, StarOutlined, FileExcelOutlined, FileSearchOutlined } from '@ant-design/icons';
import './Company.scss';
import ControlAddressDrawer from './ControlAddressDrawer';
import TrashIcon from '../../assets/icons/trash.svg';
import PenIcon from '../../assets/icons/pen.svg';
import AuthorizedPersonDrawer from './AuthorizedPersonDrawer';
import CompanyEditDrawer from './CompanyEditDrawer';
import ControlAddress from './ControlAddress';
import Notes from './Notes';
import AuthorizedPersonService from '../../services/AuthorizedPersonService';
import { notificationTypeLabels, sortNotificationTypes } from '../../constants/notificationTypes';
import { capitalizeSentences, capitalizeWords, openNotification, hasRole, sortAuthorizedPersonsByName } from '../../utils';
import PhoneNumberDisplay from '../PhoneNumberDisplay';
import ReminderDrawer, { ReminderFormValues } from '../Reminders/ReminderDrawer';
import ReminderService from '../../services/ReminderService';
import CompanyEvaluationService from '../../services/CompanyEvaluationService';
import { EVALUATION_CRITERIA } from '../../constants/companyEvaluationCriteria';
import { findBillingAddress, findCompanyAddress } from './companyBilling';
import CompanyServices from './CompanyServices';
import CompanyServiceList from './CompanyServiceList';

interface Address {
  id: number; // Address ID'yi de ekledim
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  phone: string;
  taxNumber: string;
  companyName?: string | null;
  taxOffice: string;
  title: string;
  type: any;
}

interface AuthorizedPerson {
  id: number;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phoneNumber: string;
  default: boolean;
  notificationTypes?: string[];
  controlAddressIds?: number[];
}

interface Company {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  kepAddress: string;
  invoiceIssuedToDifferentOrganization?: boolean | null;
  billingEmail?: string | null;
  expenseIncluded?: boolean | null;
  hasSupply?: boolean | null;
  withholdingRate?: number | string | null;
  withholdingCode?: string | null;
  taxOffice: string;
  taxNumber: string;
  imageId: number;
  createdAt: string;
  accountId: any;
  addresses: Address[];
  authorizedPersons: AuthorizedPerson[];
  account: any;
  ratingScore?: number | null;
  ratingLevel?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  ratingLevelLabel?: string | null;
  lastEvaluationAt?: string | null;
}

const { TabPane } = Tabs;
const { Text } = Typography;

const ratingLevelColors: Record<NonNullable<Company['ratingLevel']>, string> = {
  HIGH: '#22AD5C',
  MEDIUM: '#F59E0B',
  LOW: '#EF4444',
};

const clampRatingScore = (score?: number | null) => {
  if (score === null || score === undefined || Number.isNaN(Number(score))) {
    return null;
  }

  return Math.min(100, Math.max(0, Number(score)));
};

const CompanyDetailCard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [company, setCompany] = useState<Company | null>(null);
  const [companyId, setCompanyId] = useState<any>();
  const [image, setImage] = useState<any>('');
  const [isEditDrawerVisible, setEditDrawerVisible] = useState(false);
  const [editDrawerInitialSection, setEditDrawerInitialSection] = useState<'billing' | undefined>(undefined);
  const [isAuthorizedPersonDrawerVisible, setAuthorizedPersonDrawerVisible] = useState(false);
  const [editPerson, setEditPerson] = useState<AuthorizedPerson | null>(null);
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [authorizedPersons, setAuthorizedPersons] = useState<AuthorizedPerson[]>([]);
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState(false);
  const [reminderInitialValues, setReminderInitialValues] = useState<Partial<ReminderFormValues> | undefined>(undefined);
  const [reminderCompanyName, setReminderCompanyName] = useState<string | undefined>(undefined);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [evaluationSaving, setEvaluationSaving] = useState(false);
  const [latestEvaluationLoaded, setLatestEvaluationLoaded] = useState(false);
  const [hasPreparedReports, setHasPreparedReports] = useState(false);
  const [nonconformitySummaryLoading, setNonconformitySummaryLoading] = useState(false);
  const navigate = useNavigate();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  let userId = localStorage.getItem('id');
  const isFinance = hasRole('FINANCE');
  const isAdmin = hasRole('ADMIN');
  const isCentralUser = hasRole('CENTRAL_USER');
  const isCustomer = hasRole('CUSTOMER');
  let isPartner = localStorage.getItem('isPartner');

  const hasRequiredRole = company?.account?.roles?.some(
    (role: any) =>
      role.name === "ADMIN" || role.name === "CENTRAL_USER"
  );

  const fetchAuthorizedPersons = async () => {
    setAuthorizedPersons([]);
    try {
      const response = await AuthorizedPersonService.getCompanyAuthorizedPersons(companyId);
      const authorizedList = (Array.isArray(response?.data) ? response.data : response?.data?.content ?? []) as AuthorizedPerson[];
      const sorted = sortAuthorizedPersonsByName<AuthorizedPerson>(authorizedList);
      setAuthorizedPersons(sorted);
    } catch (error) {
      console.error('Authorized persons could not be fetched:', error);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchAuthorizedPersons();
    }
  }, [companyId]);

  const fetchLatestEvaluation = async (targetCompanyId: number) => {
    try {
      const response = await CompanyEvaluationService.getLatestByCompanyId(targetCompanyId);
      const latestAnswers = Array.isArray(response?.data?.answers) ? response.data.answers : [];
      const selectedAnswers = latestAnswers.reduce((acc: Record<number, number>, answer: any) => {
        if (answer?.criteriaId && answer?.selectedOptionId) {
          acc[Number(answer.criteriaId)] = Number(answer.selectedOptionId);
        }
        return acc;
      }, {});
      setAnswers(selectedAnswers);
      setLatestEvaluationLoaded(true);
    } catch (error: any) {
      if (error?.response?.status === 204 || error?.response?.status === 404) {
        setAnswers({});
        setLatestEvaluationLoaded(true);
        return;
      }
      console.error('Firma değerlendirmesi yüklenirken hata oluştu:', error);
    }
  };

  useEffect(() => {
    if (companyId) {
      setLatestEvaluationLoaded(false);
      fetchLatestEvaluation(Number(companyId));
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setHasPreparedReports(false);
      return;
    }
    setNonconformitySummaryLoading(true);
    CompanyService.getNonconformitySummary(companyId)
      .then((response) => {
        setHasPreparedReports(response?.data?.hasPreparedReports === true);
      })
      .catch((error) => {
        console.error('Firma uygunsuzluk özeti yüklenirken hata oluştu:', error);
        setHasPreparedReports(false);
      })
      .finally(() => {
        setNonconformitySummaryLoading(false);
      });
  }, [companyId]);

  useEffect(() => {
    if (activeTabKey === '5' && companyId && !latestEvaluationLoaded) {
      fetchLatestEvaluation(Number(companyId));
    }
  }, [activeTabKey, companyId, latestEvaluationLoaded]);

  const getControlAddress = (addressId: number | string) => {
    const targetId = Number(addressId);
    return company?.addresses?.find((item) => Number(item?.id) === targetId);
  };

  const formatControlAddressTitles = (controlAddressIds?: Array<number | string>) => {
    if (!Array.isArray(controlAddressIds) || controlAddressIds.length === 0) {
      return '-';
    }

    const titles = controlAddressIds
      .map((addressId) => String(getControlAddress(addressId)?.title || '').trim())
      .filter(Boolean);

    return titles.length > 0 ? titles.join(', ') : '-';
  };

  const toTurkishUpperText = (value?: string | null) => String(value || '').trim().toLocaleUpperCase('tr-TR');

  const getControlAddressTooltip = (controlAddressIds?: Array<number | string>) => {
    const addresses = Array.isArray(controlAddressIds)
      ? controlAddressIds
        .map((addressId) => getControlAddress(addressId))
        .filter((address): address is Address => Boolean(address))
      : [];

    if (addresses.length === 0) {
      return '-';
    }

    return (
      <div>
        {addresses.map((address, index) => {
          const addressText = [address.addressLine1, address.addressLine2].map(toTurkishUpperText).filter(Boolean).join(' ');
          const districtCity = [toTurkishUpperText(address.state), toTurkishUpperText(address.city)].filter(Boolean).join(' / ');

          return (
            <div key={address.id} style={{ marginBottom: index === addresses.length - 1 ? 0 : 8 }}>
              <div>{toTurkishUpperText(address.title) || '-'}</div>
              <div>{addressText || '-'}</div>
              <div>{districtCity || '-'}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleEditPersonClick = (person: AuthorizedPerson) => {
    setEditPerson(person);
    setAuthorizedPersonDrawerVisible(true);
  };

  const handleAuthorizedPersonDrawerClose = () => {
    setAuthorizedPersonDrawerVisible(false);
    setEditPerson(null);
    fetchAuthorizedPersons();

  };

  const handleExportAuthorizedPersons = () => {
    try {
      // Prepare header with bold style
      const headerRow: any[] = [
        { v: 'Ad', s: { font: { bold: true } } },
        { v: 'Soyad', s: { font: { bold: true } } },
        { v: 'Unvan', s: { font: { bold: true } } },
        { v: 'E-Posta', s: { font: { bold: true } } },
        { v: 'Telefon', s: { font: { bold: true } } },
        { v: 'İlgili Lokasyonlar', s: { font: { bold: true } } },
        { v: 'İletişim Konuları', s: { font: { bold: true } } },
        { v: 'Birincil', s: { font: { bold: true } } },
      ];
      // Data rows
      const rows = (authorizedPersons || []).map((p) => {
        const types = p.notificationTypes && p.notificationTypes.length
          ? sortNotificationTypes(p.notificationTypes as any)
            .map((t) => notificationTypeLabels[t as keyof typeof notificationTypeLabels])
            .join(', ')
          : '';
        return [
          p.firstName || '',
          p.lastName || '',
          p.position || '',
          p.email || '',
          p.phoneNumber || '',
          formatControlAddressTitles(p.controlAddressIds),
          types,
          p.default ? 'Evet' : 'Hayır',
        ];
      });
      const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Yetkili Bilgileri');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      const makeSafeFileName = (n: string) => n.replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
      const compName = company?.name ? makeSafeFileName(company.name) : `Firma ${companyId}`;
      const fileName = `${compName} - Yetkili Bilgileri.xlsx`;
      saveAs(blob, fileName);
    } catch (e) {
      console.error('Excel dışa aktarımında hata:', e);
      message.error('Excel dışa aktarımı başarısız oldu');
    }
  };
  const fetchCompany = async () => {
    try {
      const response = await CompanyService.getCompany(id);
      if (response.data.imageId) {
        const imageResponse = await CompanyService.getImage(response.data.imageId);
        setImage(imageResponse.data.preSignUrl);
        console.log('Logo yüklendi:', imageResponse.data.preSignUrl);
      }
      setCompanyId(response.data.id);
      setCompany(response.data);
    } catch (error) {
      console.error('Firma bilgileri yüklenirken hata oluştu:', error);
    }
  };

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const handleEditClick = (initialSection?: 'billing') => {
    setEditDrawerInitialSection(initialSection);
    setEditDrawerVisible(true);
  };

  const handleDrawerClose = () => {
    setEditDrawerVisible(false);
    setEditDrawerInitialSection(undefined);
    fetchCompany();
  };

  const handleDeleteAuthoriezPerson = async (AuthorizedPerson: AuthorizedPerson) => {
    try {
      await AuthorizedPersonService.remove(AuthorizedPerson.id);
      openNotification("success", "Başarılı", "Yetkili başarıyla silindi");
      fetchAuthorizedPersons();
      fetchCompany();
    } catch (error) {
      console.error('Yetkili silinirken hata oluştu:', error);
      openNotification("error", "Hata", 'Yetkili silinemedi')
    }
  };


  const authorizedPersonColumns = [
    {
      title: 'Yetkili Kişi',
      key: 'person',
      render: (_: any, record: any) => {
        const fullName = `${record.firstName ?? ''} ${record.lastName ?? ''}`;
        const position = record.position ?? '';

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{fullName}</div>
            <Tooltip placement="topLeft" title={position}>
              <div style={{ color: '#888' }}>{capitalizeSentences(position)}</div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'İletişim',
      key: 'contact',
      render: (_: any, record: any) => (
        <>
          <div>{record.email}</div>
          <PhoneNumberDisplay value={record.phoneNumber} />
        </>
      ),
    },
    {
      title: 'Adresler',
      dataIndex: 'controlAddressIds',
      key: 'controlAddressIds',
      render: (controlAddressIds: Array<number | string>) => {
        const titles = formatControlAddressTitles(controlAddressIds);
        return (
          <Tooltip placement="topLeft" title={getControlAddressTooltip(controlAddressIds)}>
            <span>{titles}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'İletişim Konuları',
      dataIndex: 'notificationTypes',
      key: 'notificationTypes',
      render: (types: string[]) => {
        if (!types || types.length === 0) return '-';
        const sorted = sortNotificationTypes(types as any);
        return sorted
          .map((t) => notificationTypeLabels[t as keyof typeof notificationTypeLabels])
          .join(', ');
      },
    },
    {
      title: 'İşlem',
      key: 'action',
      render: (text: string, record: AuthorizedPerson) => (
        <Space>
          <Button
            icon={<img src={PenIcon} alt="pen" className="icon" />}
            shape="circle"
            onClick={() => handleEditPersonClick(record)}
            className='icon-button'
          />

          {record.default == true ?
            <Tooltip title="Birincil yetkiliyi silemezsin">
              <Button
                icon={<img src={TrashIcon} alt="trash" className="icon" />}
                shape="circle"
                disabled
                className='icon-button'
              />
            </Tooltip> :
            authorizedPersons.length > 1 ? (
              <Popconfirm placement="left"
                title="Bu yetkiliyi silmek istediğinizden emin misiniz?"
                onConfirm={() => handleDeleteAuthoriezPerson(record)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                  shape="circle"
                  className='icon-button'
                />
              </Popconfirm>
            ) : (

              <Tooltip title="En az bir yetkili bulunmalı">
                <Button
                  icon={<img src={TrashIcon} alt="trash" className="icon" />}
                  shape="circle"
                  disabled
                  className='icon-button'
                />
              </Tooltip>
            )}
          <Popconfirm placement="left"
            title={`Bu kişiyi Birincil yetkili yapmak mı istiyorsunuz?`}
            onConfirm={() => handleSetDefault(record.id)}
            okText="Evet"
            cancelText="Hayır"
            disabled={record.default}
          >
            <Button
              icon={record.default ? <StarFilled style={{ color: '#333e48' }} /> : <StarOutlined />}
              shape="circle"
              className='icon-button'
              disabled={record.default}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleSetDefault = async (authorizedPersonId: number) => {
    try {
      await AuthorizedPersonService.setDefault(authorizedPersonId);
      openNotification("success", "Başarılı", 'Yetkili başarıyla Birincil yapıldı');
      fetchAuthorizedPersons();
    } catch (error) {
      console.error('Birincil yetkili ayarlanırken hata oluştu:', error);
      openNotification("error", "Hata", 'Birincil yetkili ayarlanamadı');
    }
  };


  const handleAddPersonClick = () => {
    setEditPerson(null); // Boş form için editPerson'u null yapıyoruz
    setAuthorizedPersonDrawerVisible(true);
  };

  const companyAddress = findCompanyAddress(company?.addresses);
  const billingAddress = findBillingAddress(company?.addresses) || companyAddress;
  const billingCompanyName = billingAddress?.companyName || company?.name || '';
  const invoiceTaxOffice = company?.taxOffice || '';
  const invoiceTaxNumber = company?.taxNumber || '';
  const billingPhoneNumber = billingAddress?.phone || company?.phoneNumber || '';
  const billingEmail = company?.billingEmail || company?.email || '';
  const billingCity = billingAddress?.city || companyAddress?.city || '';
  const billingDistrict = billingAddress?.state || companyAddress?.state || '';
  const billingAddressLine = billingAddress?.addressLine1 || companyAddress?.addressLine1 || '';
  const formatBooleanLabel = (value?: boolean | null) => {
    if (value === true) return 'EVET';
    if (value === false) return 'HAYIR';
    return '-';
  };
  const formatWithholdingRate = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') return '-';
    return `%${value}`;
  };

  const handleOfferNavigation = (id: any = null) => {
    if (id) {
      navigate(`/proposals/create?companyId=${id}`);
      return;
    }
    navigate(`/proposals/create`);
  };

  const handleEvaluationOptionSelect = (criteriaId: number, selectedOptionId: number) => {
    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [criteriaId]: selectedOptionId,
    }));
  };

  const handleEvaluationSave = async () => {
    if (!company?.id) return;

    const missingCriteria = EVALUATION_CRITERIA.filter((criteria) => !answers[criteria.id]);
    if (missingCriteria.length > 0) {
      message.error('Değerlendirmeyi kaydetmeden önce tüm soruları cevaplayın.');
      return;
    }

    try {
      setEvaluationSaving(true);
      const payload = {
        companyId: Number(company.id),
        answers: EVALUATION_CRITERIA.map((criteria) => ({
          criteriaId: criteria.id,
          selectedOptionId: answers[criteria.id],
        })),
      };
      const response = await CompanyEvaluationService.create(payload);
      const evaluation = response.data;

      setCompany((previousCompany) => previousCompany
        ? {
          ...previousCompany,
          ratingScore: evaluation.totalScore,
          ratingLevel: evaluation.ratingLevel,
          ratingLevelLabel: evaluation.ratingLevelLabel,
          lastEvaluationAt: evaluation.createdAt,
        }
        : previousCompany);
      message.success('Değerlendirme başarıyla kaydedildi.');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Değerlendirme kaydedilemedi.';
      message.error(errorMessage);
    } finally {
      setEvaluationSaving(false);
    }
  };

  const ratingScore = clampRatingScore(company?.ratingScore);
  const ratingLevelLabel = toTurkishUpperText(company?.ratingLevelLabel || company?.ratingLevel);
  const ratingChartColor = company?.ratingLevel ? ratingLevelColors[company.ratingLevel] : '#333e48';
  const ratingChartOptions: ApexOptions = {
    chart: {
      type: 'radialBar',
      sparkline: { enabled: true },
    },
    plotOptions: {
      radialBar: {
        startAngle: -110,
        endAngle: 110,
        hollow: { size: '58%' },
        track: {
          background: '#eef1f5',
          strokeWidth: '70%',
          margin: 2,
        },
        dataLabels: {
          show: true,
          name: { show: false },
          value: {
            show: true,
            offsetY: 8,
            color: '#232b3e',
            fontFamily: "'Urbanist', sans-serif",
            fontSize: '20px',
            fontWeight: 800,
            formatter: (value: number) => `${Math.round(value)}`,
          },
        },
      },
    },
    colors: [ratingChartColor],
    labels: ['Değerlendirme'],
    stroke: { lineCap: 'round' },
  };

  const handleOpenReminderDrawer = () => {
    if (!company?.id) return;
    const idNumber = Number(company.id);
    setReminderInitialValues({
      activityType: 'CALL',
      entityTab: 'COMPANY',
      companyId: Number.isNaN(idNumber) ? undefined : idNumber,
    });
    setReminderCompanyName(company.name || company.id?.toString());
    setReminderDrawerOpen(true);
  };

  const handleReminderSubmit = async (values: ReminderFormValues) => {
    try {
      const entityId = values.companyId;
      const isMeeting = values.activityType === 'MEETING_OFFLINE' || values.activityType === 'MEETING_ONLINE';
      const channelDefault = values.activityType === 'MEETING_OFFLINE' ? 'IN_PERSON' : values.activityType === 'MEETING_ONLINE' ? 'ONLINE' : '';
      const resolvedChannel = values.channel ?? (isMeeting ? channelDefault : undefined);
      const reminderDate = values.reminderDate
        ? values.reminderDate.format('YYYY-MM-DDTHH:mm:ss')
        : undefined;
      const dueDate = values.dueDate ? values.dueDate.format('YYYY-MM-DDTHH:mm:ss') : undefined;
      const properties =
        values.properties && typeof values.properties === 'object'
          ? JSON.stringify(values.properties)
          : values.properties;
      const payload = {
        content: values.content,
        reminderDate,
        dueDate,
        estimatedDurationMinutes: values.estimatedDurationMinutes,
        activityType: values.activityType,
        channel: resolvedChannel,
        link: isMeeting ? values.link : undefined,
        state: 'PENDING' as const,
        ...(values.statusId ? { statusId: values.statusId } : {}),
        ...(properties ? { properties } : {}),
        ...(entityId ? { entityName: 'COMPANY', entityId } : {}),
      };

      if (isAdmin) {
        await ReminderService.createForAccount({ ...payload, accountId: values.accountId });
      } else {
        await ReminderService.createForMe(payload);
      }

      openNotification('success', 'Başarılı', 'Görev oluşturuldu');
      setReminderDrawerOpen(false);
      setReminderInitialValues(undefined);
    } catch (e: any) {
      if (e?.errorFields) return;
      openNotification('error', 'Hata', e?.response?.data || 'Görev oluşturulamadı');
    }
  };

  return (
    <Card className={`company-card p-20px br-15px ${activeTabKey === '5' ? 'company-evaluation-tab-active' : ''}`} >
      {company && (
        <>

          <div className='mb-20px'>
            <Row justify='space-between' align='middle' gutter={[16, 16]}>
              <div >
                <Space size={10} style={{ flexWrap: 'wrap' }}>
                  <span className='d-flex align-items-center'>
                    <Avatar className='company-avatar'>
                      {company.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Text strong className='fs-24px ml-10px'>
                      <Tooltip title={company.name.toUpperCase()}>
                        <span>
                          {company.name.length > 40 ? `${company.name.substring(0, 40).toUpperCase()}...` : company.name.toUpperCase()}
                        </span>
                      </Tooltip>
                    </Text>
                  </span>


                  {activeTabKey === '1' && isMobile ? (
                    <Button
                      icon={<img src={PenIcon} alt="pen" className="icon" />}
                      shape="circle"
                      onClick={() => handleEditClick()}
                      className='icon-button'
                    />
                  ) : null}
                </Space>
              </div>
              <Space size={15} wrap>
                <div>
                  {ratingScore !== null ? (
                      <Tooltip title={ratingLevelLabel || ''}>
                      <Chart
                        options={ratingChartOptions}
                        series={[ratingScore]}
                        type="radialBar"
                        height={80}
                        width={100}
                      />
                      </Tooltip>
                  ) : (
                    <Text strong>Henüz yok</Text>
                  )}
                </div>
                {!isFinance && (
                  hasRequiredRole ? (
                    <Button
                      type="primary"
                      onClick={() => handleOfferNavigation(companyId)}
                      disabled={!hasRequiredRole}
                    >
                      Teklif Oluştur
                    </Button>
                  ) : (
                    <Tooltip title={isPartner?.toString() === 'false' ? 'Teklif oluşturabilmek için profil sayfanızda yer alan Sözleşme bölümündeki İş Ortaklığı Sözleşmesini onaylamanız gerekmektedir.' : ''}>
                      <Button
                        type="primary"
                        onClick={() => handleOfferNavigation(companyId)}
                        disabled={(company.accountId != userId || isPartner?.toString() === 'false')}
                      >
                        Teklif Oluştur
                      </Button>
                    </Tooltip>
                  )
                )}
                {(isAdmin || isCentralUser) && !isCustomer && (
                  <Button
                    type="default"
                    className="action-button"
                    onClick={handleOpenReminderDrawer}
                  >
                    Görev Ekle
                  </Button>
                )}
                {activeTabKey === '1' && !isMobile ? (
                  <Button
                    icon={<img src={PenIcon} alt="pen" className="icon" />}
                    shape="circle"
                    onClick={() => handleEditClick()}
                    className='icon-button'
                  />
                ) : null}
              </Space>
            </Row>
          </div>
          <Tabs defaultActiveKey="1" onChange={(key) => setActiveTabKey(key)} tabBarExtraContent={{
            right: (
              <Space size={8} wrap>
                <Tooltip title={!hasPreparedReports && !nonconformitySummaryLoading ? 'Daha öncesinde rapor hazırlanmamış.' : ''}>
                  <span>
                    <Button
                      icon={<FileSearchOutlined />}
                      onClick={() => navigate(`/companies/${companyId}/control-reports`)}
                      disabled={!hasPreparedReports}
                      loading={nonconformitySummaryLoading}
                      className='d-button action-button'
                    >
                      Kontrol Raporları
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={!hasPreparedReports && !nonconformitySummaryLoading ? 'Daha öncesinde rapor hazırlanmamış.' : ''}>
                  <span>
                    <Button
                      icon={<FileSearchOutlined />}
                      onClick={() => navigate(`/companies/${companyId}/nonconformities`)}
                      disabled={!hasPreparedReports}
                      loading={nonconformitySummaryLoading}
                      className='d-button action-button'
                    >
                      Uygunsuzlukları Listele
                    </Button>
                  </span>
                </Tooltip>
                <Button onClick={() => navigate('/companies')} className='d-button action-button'>
                  Geri
                </Button>
              </Space>
            ),
          }}>
            <TabPane tab="Firma Bilgileri" key="1">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Firma Unvanı</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    {company.name.toUpperCase()}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Firma E-posta Adresi</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{company.email}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Firma Telefon Numarası</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    <PhoneNumberDisplay value={company.phoneNumber} />
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">İl</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{capitalizeSentences(companyAddress?.city) || ""}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">İlçe</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{capitalizeSentences(companyAddress?.state) || ""}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Adres</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{capitalizeWords(companyAddress?.addressLine1) || ""}</div>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Fatura Bilgileri" key="6">
              <Row justify="end" style={{ marginBottom: 16 }}>
                <Button
                  icon={<img src={PenIcon} alt="pen" className="icon" />}
                  onClick={() => handleEditClick('billing')}
                  className='d-button action-button'
                >
                  Düzenle
                </Button>
              </Row>
              <Row gutter={[16, 16]}>
                {/* <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Fatura Türü</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    {company.invoiceIssuedToDifferentOrganization ? 'Farklı kişi/kuruluş' : 'Hizmet sunulan kuruluş'}
                  </div>
                </Col> */}
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Firma Unvanı</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    {billingCompanyName.toLocaleUpperCase('tr-TR')}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Vergi Dairesi</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{capitalizeSentences(invoiceTaxOffice)}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Vergi Numarası</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{invoiceTaxNumber}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Telefon Numarası</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    <PhoneNumberDisplay value={billingPhoneNumber} />
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">E-posta</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{billingEmail}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">KEP Adresi</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{company.kepAddress}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">İl</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{capitalizeSentences(billingCity)}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">İlçe</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{capitalizeSentences(billingDistrict)}</div>
                </Col>
                <Col xs={24} sm={12} lg={12}>
                  <Text type="secondary">Fatura Adresi</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>{capitalizeWords(billingAddressLine)}</div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Masraf Ekleniyor Mu?</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    {formatBooleanLabel(company.expenseIncluded)}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Tevkifat Var Mı?</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    {formatBooleanLabel(company.hasSupply)}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Tevkifat Oranı</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', width: '100%' }}>
                    {company.hasSupply ? formatWithholdingRate(company.withholdingRate) : '-'}
                  </div>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                  <Text type="secondary">Tevkifat Kodu</Text>
                  <div style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap', width: '100%' }}>
                    {company.hasSupply ? company.withholdingCode || '-' : '-'}
                  </div>
                </Col>
              </Row>
            </TabPane>
            <TabPane tab="Kontrol Adresi" key="2">
              <ControlAddress
                companyId={companyId}
                type={false}
                setSelectedAddress={() => { }}
                selectedAddressId={null}
                isEdit={false}
                titleClickable={false}
              />
            </TabPane>
            <TabPane tab="Yetkili Bilgileri" key="3">
              <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col>
                  <Button
                    type="primary"
                    className="d-button dark-button width-auto"
                    onClick={handleAddPersonClick}
                  >
                    Yeni Yetkili Ekle
                  </Button>
                </Col>
                <Col>
                  <Tooltip title="Dışa Aktar">
                    <Button
                      shape="circle"
                      className='icon-button'
                      onClick={handleExportAuthorizedPersons}
                      disabled={!authorizedPersons || authorizedPersons.length === 0}
                      aria-label="Excel indir"
                    >
                      <FileExcelOutlined />
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
              <Table
                scroll={{ x: 1080 }}
                columns={authorizedPersonColumns}
                dataSource={authorizedPersons}
                rowKey={(record) => record.email}
                pagination={false}
              />
            </TabPane>
            <TabPane tab="Notlar" key="4">
              <Notes />
            </TabPane>
            {!isCustomer && (
              <TabPane tab="Hizmetler" key="7">
                <CompanyServices companyId={companyId} />
              </TabPane>
            )}
            {!isCustomer && (
              <TabPane tab="Hizmetler Listesi" key="8">
                <CompanyServiceList companyId={companyId} />
              </TabPane>
            )}
            <TabPane tab="Değerlendirme" key="5">
              <div className="company-evaluation-section">
                <Row gutter={[16, 16]}>
                  {EVALUATION_CRITERIA.map((criteria) => (
                    <Col xs={24} lg={12} key={criteria.id}>
                      <Card className="company-evaluation-card" bordered>
                        <div className="company-evaluation-card-header">
                          <Text strong>{criteria.id}. {criteria.question}</Text>
                          <Text type="secondary">{criteria.weight} puan</Text>
                        </div>
                        <Space size={[8, 8]} wrap>
                          {criteria.options.map((option) => {
                            const isSelected = answers[criteria.id] === option.id;
                            return (
                              <Tooltip key={option.id} title={`${option.score} puan`}>
                                <Button
                                  shape="round"
                                  type={isSelected ? 'primary' : 'default'}
                                  className={`company-evaluation-option ${isSelected ? 'selected' : ''}`}
                                  onClick={() => handleEvaluationOptionSelect(criteria.id, option.id)}
                                >
                                  {option.label}
                                </Button>
                              </Tooltip>
                            );
                          })}
                        </Space>
                      </Card>
                    </Col>
                  ))}
                </Row>
                <div className="company-evaluation-actions">
                  <Button
                    type="primary"
                    className="d-button dark-button width-auto"
                    loading={evaluationSaving}
                    onClick={handleEvaluationSave}
                  >
                    Kaydet
                  </Button>
                </div>
              </div>
            </TabPane>
          </Tabs>
        </>
      )
      }

      <AuthorizedPersonDrawer
        visible={isAuthorizedPersonDrawerVisible}
        onClose={handleAuthorizedPersonDrawerClose}
        companyId={id as string}
        existingData={company as any}
        editPerson={editPerson}
      />
      <CompanyEditDrawer
        visible={isEditDrawerVisible}
        onClose={handleDrawerClose}
        companyId={id as string}
        initialSection={editDrawerInitialSection}
      />
      <ReminderDrawer
        open={reminderDrawerOpen}
        isEdit={false}
        isAdmin={isAdmin}
        companies={company ? [company] : []}
        proposals={[]}
        initialValues={reminderInitialValues}
        hideEntityTabs
        fixedCompanyId={reminderInitialValues?.companyId}
        fixedCompanyName={reminderCompanyName}
        onClose={() => setReminderDrawerOpen(false)}
        onSubmit={handleReminderSubmit}
      />
    </Card >
  );
};

export default CompanyDetailCard;
